"use client";

import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import useFetchWithAuth from "../../features/hooks/useFetchWithAuth";
import fetchWithAuth from "../../util/fetchWithAuth";
import { API_URL } from "../../util/url";
import "./WorkScheduleTable.css";

const UncontrolledInput = React.memo(
  ({
    defaultValue,
    placeholder,
    onCommit,
    onChange,
    onEnter,
    onKeyNav,
    inputProps,
  }) => {
    const ref = useRef(null);
    const commit = useCallback((reason = "blur") => {
      if (!ref.current) return;
      onCommit(ref.current.value, { reason });
    }, [onCommit]);

    const handleKeyDown = useCallback(
      (event) => {
        if (event.key === "Enter") {
          event.preventDefault();
          commit("enter");
          if (onEnter) {
            onEnter(event);
            return;
          }
        }
        onKeyNav?.(event);
      },
      [commit, onEnter, onKeyNav]
    );

    const { className, ...restInputProps } = inputProps || {};
    const inputClassName = className ? `ws-input ${className}` : "ws-input";
    return (
      <input
        className={inputClassName}
        defaultValue={defaultValue}
        placeholder={placeholder}
        onChange={onChange}
        onBlur={commit}
        onKeyDown={handleKeyDown}
        ref={ref}
        {...restInputProps}
      />
    );
  }
);

const parseTimeToMinutes = (token) => {
  const raw = String(token || "").trim();
  if (!raw) return null;

  // HH:MM format
  if (raw.includes(":")) {
    const [hoursPart, minutesPart = "0"] = raw.split(":");
    const hours = Number(hoursPart);
    const minutes = Number(minutesPart);
    if (
      !Number.isFinite(hours) ||
      !Number.isFinite(minutes) ||
      minutes < 0 ||
      minutes >= 60
    ) {
      return null;
    }
    return hours * 60 + minutes;
  }

  // Decimal hours: 10,5 / 10.5
  if (raw.includes(",") || raw.includes(".")) {
    const normalized = raw.replace(",", ".");
    const value = Number(normalized);
    if (!Number.isFinite(value)) return null;
    return Math.round(value * 60);
  }

  const hours = Number(raw);
  if (!Number.isFinite(hours)) return null;
  return hours * 60;
};

const parseShiftRanges = (value) => {
  if (!value) return null;
  const trimmed = String(value)
    .replace(/[–—−]/g, "-")
    .trim();
  if (!trimmed) return null;
  const ranges = [];

  const segments = trimmed
    .split(/[\/|;]/)
    .map((segment) => segment.trim())
    .filter(Boolean);

  segments.forEach((segment) => {
    const parts = segment.split("-").map((part) => part.trim());
    if (parts.length !== 2) return;
    const start = parseTimeToMinutes(parts[0]);
    const end = parseTimeToMinutes(parts[1]);
    if (start != null && end != null && end > start) {
      ranges.push({ start, end });
    }
  });

  return ranges.length ? ranges : null;
};

const getShiftStats = (shiftValue) => {
  const key = String(shiftValue || "").trim();
  if (!key) return null;
  if (SHIFT_STATS_CACHE.has(key)) {
    return SHIFT_STATS_CACHE.get(key);
  }
  const ranges = parseShiftRanges(shiftValue);
  if (!ranges) {
    SHIFT_STATS_CACHE.set(key, null);
    return null;
  }
  let totalMinutes = 0;
  let minStart = Infinity;
  let maxEnd = -Infinity;
  ranges.forEach((range) => {
    totalMinutes += range.end - range.start;
    minStart = Math.min(minStart, range.start);
    maxEnd = Math.max(maxEnd, range.end);
  });
  const result = {
    ranges,
    totalMinutes,
    start: minStart,
    end: maxEnd,
  };
  SHIFT_STATS_CACHE.set(key, result);
  return result;
};

const toMinutes = (value) => {
  if (!value) return null;
  const stats = getShiftStats(value);
  if (!stats) return null;
  return stats.totalMinutes > 0 ? stats.totalMinutes : null;
};

const isMakeupShift = (shiftValue) => {
  const trimmed = String(shiftValue || "").trim();
  if (!trimmed) return false;
  return /відпрац|відпр/i.test(trimmed) || /^[vbв]\s*-?\s*\d/i.test(trimmed);
};

const formatPlanValue = (shiftValue) => {
  const normalized = String(shiftValue || "").trim().toLowerCase();
  if (!normalized) return "В";
  if (/^[vbв]$/.test(normalized)) return "В";
  const minutes = toMinutes(shiftValue);
  if (!minutes) return "";
  const hours = minutes / 60;
  return Number.isInteger(hours) ? String(hours) : String(hours);
};

const resolveUserId = (userValue) => {
  if (userValue && typeof userValue === "object") {
    return userValue.id ?? userValue.user_id ?? userValue.operator_id ?? null;
  }
  return userValue ?? null;
};

const getEntryUserId = (entry) =>
  resolveUserId(
    entry?.user ?? entry?.user_id ?? entry?.operator ?? entry?.operator_id
  );

const planHoursFromShift = (shiftValue) => {
  if (isMakeupShift(shiftValue)) return 0;
  const minutes = toMinutes(shiftValue);
  if (!minutes) return null;
  return minutes / 60;
};

const planHoursFromShiftAll = (shiftValue) => {
  const minutes = toMinutes(shiftValue);
  if (!minutes) return 0;
  return minutes / 60;
};

const parseNumber = (value) => {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : null;
};

const parseFactValue = (value) => {
  if (value === null || value === undefined) return null;
  const raw = String(value).trim();
  if (!raw) return null;
  const minutes = toMinutes(raw);
  if (minutes) return minutes / 60;
  const parsed = Number(raw.replace(",", "."));
  return Number.isFinite(parsed) ? parsed : null;
};

const parsePlanValue = (value) => {
  if (value === null || value === undefined) return null;
  const raw = String(value).trim();
  if (!raw) return null;
  const parsed = Number(raw.replace(",", "."));
  return Number.isFinite(parsed) ? parsed : null;
};

const getPlanNumeric = (entry, shiftValue) => {
  const fromPlan = parsePlanValue(entry?.plan_people);
  if (fromPlan != null) return fromPlan;
  const fromShift = planHoursFromShiftAll(shiftValue);
  return Number.isFinite(fromShift) ? fromShift : 0;
};

const formatHoursDelta = (value) => {
  const rounded = Math.round(value * 10) / 10;
  if (rounded === 0) return "0";
  return `${rounded > 0 ? "+" : ""}${rounded}`;
};

const formatHoursValue = (value) => {
  const rounded = Math.round(value * 10) / 10;
  if (!Number.isFinite(rounded)) return "0";
  return Number.isInteger(rounded) ? String(rounded) : String(rounded);
};

const normalizeSearchValue = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s]/gu, " ")
    .replace(/\s+/g, " ")
    .trim();

const getPlanBackground = (shiftValue, planHours, factValue) => {
  const normalized = String(shiftValue || "").trim().toLowerCase();
  if (!normalized) return "#ffffff";
  if (/^[vbв]$/.test(normalized)) return "#fef9c3";
  if (planHours == null || factValue == null) return "#ede9fe";
  const diff = factValue - planHours;
  if (Math.abs(diff) < 0.01) return "#dcfce7";
  if (diff > 0) return "#dbeafe";
  return "#fee2e2";
};

const getFactBackground = (shiftValue, planHours, factValue) => {
  if (factValue == null) return "#ffffff";
  const normalized = String(shiftValue || "").trim().toLowerCase();
  if (!normalized) return "#ffffff";
  if (planHours == null) return "#ede9fe";
  const diff = factValue - planHours;
  if (Math.abs(diff) < 0.01) return "#dcfce7";
  if (diff > 0) return "#dbeafe";
  return "#fee2e2";
};

const WEEKDAY_LABELS = ["НД", "ПН", "ВТ", "СР", "ЧТ", "ПТ", "СБ"];
const getWeekdayLabel = (dateValue) =>
  WEEKDAY_LABELS[dayjs(dateValue).day()] || "";

const PERIODS = [
  { key: "morning", label: "Ранок", start: 6 * 60, end: 12 * 60 },
  { key: "day", label: "День", start: 12 * 60, end: 18 * 60 },
  { key: "evening", label: "Вечір", start: 18 * 60, end: 24 * 60 },
];

const classifyShiftPeriod = (shiftValue) => {
  const normalized = String(shiftValue || "").trim();
  if (!normalized) return null;

  // Google Sheets parity:
  // morning: ^0?[89]
  // day: not morning and not ending with 21
  // evening: 21$
  if (/^0?[89]/.test(normalized)) return "morning";
  if (/21$/.test(normalized)) return "evening";
  return "day";
};

const COMMENTS_STORAGE_KEY = "workScheduleComments";
const SHIFT_STATS_CACHE = new Map();
const DAY_COL_WIDTH = 220;
const DAY_OVERSCAN = 4;
const AUTOSAVE_DELAY_MS = 1800;
const DRAG_WINDOW_UPDATE_THROTTLE_MS = 120;
const DAY_WINDOW_CHUNK = 3;
const SCHEDULE_CACHE_TTL_MS = 30000;
const OPERATORS_CACHE_KEY = "workScheduleOperatorsCacheV1";
const GSHEET_SYNC_ENDPOINT = `${API_URL}/statistic/schedule/sync-gsheet/`;
const GSHEET_RENDER_ENDPOINT = `${API_URL}/statistic/schedule/gsheet-sheet-render/`;
const GSHEET_STICKY_HEADER_ROWS = 5;
const GSHEET_STICKY_LEFT_COLUMNS = 2;
const GSHEET_DEFAULT_ROW_HEIGHT = 22;
const normalizeDraftFieldValue = (field, value) => {
  if (field === "fact_people") {
    return String(value ?? "").trim();
  }
  return String(value ?? "");
};

const extractSyncErrorMessage = (payload, fallback = "Помилка синхронізації") => {
  if (!payload) return fallback;
  if (typeof payload === "string") return payload;
  if (Array.isArray(payload?.detail)) {
    return payload.detail.join(" ");
  }
  if (typeof payload?.detail === "string") return payload.detail;
  if (typeof payload?.error_text === "string") return payload.error_text;
  if (typeof payload?.message === "string") return payload.message;
  return fallback;
};
const normalizeMonth = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return dayjs().format("MM.YYYY");
  let year;
  let month;
  const mmYyyy = raw.match(/^(\d{1,2})[.\-/](\d{4})$/);
  if (mmYyyy) {
    month = Number(mmYyyy[1]);
    year = Number(mmYyyy[2]);
  } else {
    const yyyyMm = raw.match(/^(\d{4})[.\-/](\d{1,2})(?:[.\-/]\d{1,2})?$/);
    if (yyyyMm) {
      year = Number(yyyyMm[1]);
      month = Number(yyyyMm[2]);
    }
  }
  if (Number.isFinite(year) && Number.isFinite(month)) {
    if (month >= 1 && month <= 12) {
      return dayjs(`${year}-${String(month).padStart(2, "0")}-01`).format(
        "MM.YYYY"
      );
    }
  }
  const fallback = dayjs(raw);
  return fallback.isValid() ? fallback.format("MM.YYYY") : dayjs().format("MM.YYYY");
};

const to255 = (channel) => {
  const value = Number(channel);
  if (!Number.isFinite(value)) return 0;
  if (value <= 1) return Math.round(value * 255);
  return Math.max(0, Math.min(255, Math.round(value)));
};

const toAlpha = (alpha) => {
  const value = Number(alpha);
  if (!Number.isFinite(value)) return 1;
  if (value <= 1) return Math.max(0, Math.min(1, value));
  return Math.max(0, Math.min(1, value / 255));
};

const gsheetColorToCss = (value) => {
  if (!value) return undefined;
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed || undefined;
  }

  if (typeof value !== "object") return undefined;
  const rgb = value.rgbColor || value.color || value;
  const red = to255(rgb.red ?? rgb.r);
  const green = to255(rgb.green ?? rgb.g);
  const blue = to255(rgb.blue ?? rgb.b);
  const alpha = toAlpha(
    rgb.alpha ??
      rgb.a ??
      rgb.alphaValue ??
      value.alpha ??
      value.a
  );
  if (alpha < 1) return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
  return `rgb(${red}, ${green}, ${blue})`;
};

const pickGsheetColor = (...candidates) => {
  for (const candidate of candidates) {
    const css = gsheetColorToCss(candidate);
    if (css) return css;
  }
  return undefined;
};

const getMonthStart = (monthValue) => {
  const normalized = normalizeMonth(monthValue);
  const [mm, yyyy] = normalized.split(".");
  return dayjs(`${yyyy}-${mm}-01`);
};

const buildLocalValues = (orderedDays, scheduleMap, draftMap) => {
  const next = {};
  orderedDays.forEach((day) => {
    const entry = scheduleMap[day.date] || {};
    const draft = draftMap?.[day.date] || {};
    next[day.date] = {
      shift: draft.shift ?? entry.shift ?? "",
      plan_people: entry.plan_people ?? "",
      fact_people: draft.fact_people ?? entry.fact_people ?? "",
    };
  });
  return next;
};

const WorkScheduleRow = React.memo(
  ({
    person,
    orderedDays,
    visibleDays,
    dayStartIndex,
    leftSpacerWidth,
    rightSpacerWidth,
    scheduleMap,
    draftsRef,
    readOnly,
    onDraftChange,
    onShiftChange,
    onFactChange,
    rowIndex,
    rowCount,
    tableRef,
    balanceHours,
    showBalance,
    comment,
    showComment,
    onEditComment,
  }) => {
    const renderDays = visibleDays?.length ? visibleDays : orderedDays;
    const startIndex = Number.isFinite(dayStartIndex) ? dayStartIndex : 0;
    const [localValues, setLocalValues] = useState(() =>
      buildLocalValues(orderedDays, scheduleMap, draftsRef?.current?.[person.user])
    );
    const pendingUpdatesRef = useRef({});
    const rafRef = useRef(null);

    useEffect(() => {
      setLocalValues(
        buildLocalValues(orderedDays, scheduleMap, draftsRef?.current?.[person.user])
      );
    }, [orderedDays, scheduleMap, draftsRef, person.user]);

    useEffect(() => {
      return () => {
        if (rafRef.current) {
          cancelAnimationFrame(rafRef.current);
        }
      };
    }, []);

    const flushPendingUpdates = useCallback(() => {
      const updates = pendingUpdatesRef.current;
      pendingUpdatesRef.current = {};
      setLocalValues((prev) => {
        const next = { ...prev };
        Object.entries(updates).forEach(([date, patch]) => {
          const current = next[date] || {
            shift: "",
            plan_people: "",
            fact_people: "",
          };
          next[date] = { ...current, ...patch };
        });
        return next;
      });
    }, []);

    const applyCommit = useCallback(
      (date, field, value) => {
        const currentPatch = pendingUpdatesRef.current[date] || {};
        pendingUpdatesRef.current = {
          ...pendingUpdatesRef.current,
          [date]: { ...currentPatch, [field]: value },
        };
        if (!rafRef.current) {
          rafRef.current = requestAnimationFrame(() => {
            rafRef.current = null;
            flushPendingUpdates();
          });
        }
        onDraftChange(person.user, date, field, value);
      },
      [flushPendingUpdates, onDraftChange, person.user]
    );

    const handleKeyNav = useCallback(
      (event) => {
        const { key } = event;
        if (!["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(key)) {
          return;
        }
        if (key.startsWith("Arrow") && !(event.altKey || event.ctrlKey || event.metaKey)) {
          return;
        }

        const target = event.currentTarget;
        const row = Number(target.getAttribute("data-row"));
        const day = Number(target.getAttribute("data-day"));
        const field = target.getAttribute("data-field");
        if (!Number.isFinite(row) || !Number.isFinite(day) || !field) return;

        const fields = ["shift", "fact_people"];
        let fieldIndex = fields.indexOf(field);
        if (fieldIndex === -1) return;

        let nextRow = row;
        let nextDay = day;

        if (key === "ArrowRight") {
          if (fieldIndex < fields.length - 1) {
            fieldIndex += 1;
          } else if (day < orderedDays.length - 1) {
            nextDay += 1;
            fieldIndex = 0;
          } else {
            return;
          }
        }

        if (key === "ArrowLeft") {
          if (fieldIndex > 0) {
            fieldIndex -= 1;
          } else if (day > 0) {
            nextDay -= 1;
            fieldIndex = fields.length - 1;
          } else {
            return;
          }
        }

        if (key === "ArrowDown") {
          if (row < rowCount - 1) {
            nextRow += 1;
          } else {
            return;
          }
        }

        if (key === "ArrowUp") {
          if (row > 0) {
            nextRow -= 1;
          } else {
            return;
          }
        }

        const selector = `input[data-row="${nextRow}"][data-day="${nextDay}"][data-field="${fields[fieldIndex]}"]`;
        const nextInput = tableRef?.current?.querySelector(selector);
        if (nextInput) {
          event.preventDefault();
          nextInput.focus();
          if (typeof nextInput.select === "function") {
            nextInput.select();
          }
        }
      },
      [orderedDays.length, rowCount, tableRef]
    );

    const focusShiftCell = useCallback(
      (targetDayIndex) => {
        const selector = `input[data-row="${rowIndex}"][data-day="${targetDayIndex}"][data-field="shift"]`;
        const nextInput = tableRef?.current?.querySelector(selector);
        if (!nextInput) return;
        nextInput.focus();
        if (typeof nextInput.select === "function") nextInput.select();
      },
      [rowIndex, tableRef]
    );

    const moveToNextDayShift = useCallback(
      (currentDayIndex) => {
        const nextDay = currentDayIndex + 1;
        if (nextDay >= orderedDays.length) return;
        const container = tableRef?.current;
        if (container) {
          container.scrollLeft += DAY_COL_WIDTH;
        }
        requestAnimationFrame(() => focusShiftCell(nextDay));
      },
      [focusShiftCell, orderedDays.length, tableRef]
    );

    const commitShift = useCallback(
      (date, value, meta, absoluteDayIndex) => {
        applyCommit(date, "shift", value);
        if (meta?.reason !== "change") {
          onShiftChange(person.user, date, value);
        }
        if (meta?.reason === "enter") {
          moveToNextDayShift(absoluteDayIndex);
        }
      },
      [applyCommit, onShiftChange, person.user, moveToNextDayShift]
    );

    const commitFactPeople = useCallback(
      (date, value, meta) => {
        applyCommit(date, "fact_people", value);
        if (meta?.reason !== "change") {
          onFactChange(person.user, date, value);
        }
      },
      [applyCommit, onFactChange, person.user]
    );

    return (
      <TableRow>
        <TableCell
          className={`text-black border ws-name-cell ws-sticky-name ws-sticky-cell ${
            readOnly ? "ws-sticky-name-only" : ""
          } ${showComment && comment ? "ws-has-comment" : ""}`}
          title={showComment && comment ? comment : undefined}
        >
          <div className="ws-name-inner">

            <div className="ws-name-left">
              <span className="ws-name-text">{person.name}</span>

              {showBalance && typeof balanceHours === "number" && balanceHours !== 0 && (
                <span
                  className={`ws-balance ${
                    balanceHours < 0
                      ? "ws-balance-negative"
                      : balanceHours > 0
                      ? "ws-balance-positive"
                      : "ws-balance-neutral"
                  }`}
                >
                  {formatHoursDelta(balanceHours)} год
                </span>
              )}
            </div>

            <div className="ws-name-right">
              {showComment && comment && (
                <span
                  className="ws-comment-dot"
                  title={comment}
                />
              )}

              {showComment && !readOnly && (
                <IconButton
                  size="small"
                  onClick={() => onEditComment?.(person.user)}
                  className="ws-comment-edit"
                  aria-label="Редагувати коментар"
                  title="Редагувати коментар"
                >
                  <EditOutlinedIcon fontSize="inherit" />
                </IconButton>
              )}
            </div>

          </div>
        </TableCell>

        {leftSpacerWidth > 0 && (
          <TableCell
            className="text-black border ws-spacer-cell"
            style={{ minWidth: leftSpacerWidth }}
          />
        )}

        {renderDays.map((day, dayIndex) => (
          <WorkScheduleCell
            key={day.date}
            readOnly={readOnly}
            day={day}
            entry={localValues[day.date]}
            rowIndex={rowIndex}
            dayIndex={dayIndex + startIndex}
            onCommitShift={commitShift}
            onCommitFact={commitFactPeople}
            onKeyNav={handleKeyNav}
          />
        ))}

        {rightSpacerWidth > 0 && (
          <TableCell
            className="text-black border ws-spacer-cell"
            style={{ minWidth: rightSpacerWidth }}
          />
        )}
      </TableRow>
    );
  }
);

const WorkScheduleCell = React.memo(
  ({
    readOnly,
    day,
    entry,
    rowIndex,
    dayIndex,
    onCommitShift,
    onCommitFact,
    onKeyNav,
  }) => {
    const value = entry || {
      shift: "",
      plan_people: "",
      fact_people: "",
    };
    const shiftValue = value.shift ?? "";
    const planValue = formatPlanValue(shiftValue);
    const planHours = planHoursFromShift(shiftValue);
    const factNumber = parseFactValue(value.fact_people);
    const planBackground = getPlanBackground(
      shiftValue,
      planHours,
      factNumber
    );
    const dayDate = day?.date;

    return (
      <TableCell className="text-black border ws-cell">
        {readOnly ? (
          <div className="ws-cell-readonly">
            <span className="ws-cell-text">
              {shiftValue ? shiftValue : "-"}
            </span>
            <span
              className="ws-pill ws-cell-text"
              style={{ backgroundColor: planBackground }}
            >
              {planValue}
            </span>
            <span className="ws-cell-text">
              {value.fact_people ?? "-"}
            </span>
          </div>
        ) : (
          <div className="ws-cell-edit">
            <div className="ws-input-box">
              <UncontrolledInput
                defaultValue={shiftValue}
                placeholder=""
                onChange={(event) =>
                  onCommitShift(
                    day.date,
                    event.target.value,
                    { reason: "change" },
                    dayIndex
                  )
                }
                onCommit={(nextValue, meta) =>
                  onCommitShift(day.date, nextValue, meta, dayIndex)
                }
                onEnter={() => {}}
                onKeyNav={onKeyNav}
                inputProps={{
                  "data-row": rowIndex,
                  "data-day": dayIndex,
                  "data-field": "shift",
                  className: "ws-shift-input",
                  style: { textAlign: "center", fontSize: 14 },
                }}
              />
            </div>

            <div className="ws-input-box">
              <input
                className="ws-input ws-plan-input"
                value={planValue}
                readOnly
                style={{
                  textAlign: "center",
                  opacity: 1,
                  backgroundColor: planBackground,
                  borderRadius: 4,
                  borderColor: planBackground,
                }}
              />
            </div>

            <div className="ws-input-box">
              <UncontrolledInput
                defaultValue={value.fact_people ?? ""}
                onChange={(event) =>
                  onCommitFact(dayDate, event.target.value, { reason: "change" })
                }
                onCommit={(nextValue, meta) => onCommitFact(dayDate, nextValue, meta)}
                onKeyNav={onKeyNav}
                inputProps={{
                  "data-row": rowIndex,
                  "data-day": dayIndex,
                  "data-field": "fact_people",
                  style: { textAlign: "center", fontSize: 14 },
                }}
              />
            </div>
          </div>
        )}
      </TableCell>
    );
  }
);

const WorkScheduleTable = ({
  title = "Графік роботи",
  month,
  readOnly = false,
  filterUserId = null,
  dayOrder = "asc",
  scheduleEndpoint = "/statistic/schedule/",
  usersEndpoint = "/user/operators/",
}) => {
  const tableRef = useRef(null);
  const [operatorsList, setOperatorsList] = useState([]);
  const [scheduleByUser, setScheduleByUser] = useState({});
  const [saving, setSaving] = useState({});
  const [operatorSearch, setOperatorSearch] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [urlOperatorId, setUrlOperatorId] = useState(null);
  const [selectedClearUserId, setSelectedClearUserId] = useState("");
  const [clearingSchedule, setClearingSchedule] = useState(false);
  const draftsRef = useRef({});
  const [operators, setOperators] = useState([]);
  const [operatorsLoading, setOperatorsLoading] = useState(false);
  const [cachedScheduleData, setCachedScheduleData] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(() =>
    normalizeMonth(month)
  );
  const [commentsByUser, setCommentsByUser] = useState({});
  const [syncRunning, setSyncRunning] = useState(false);
  const [syncMessage, setSyncMessage] = useState("");
  const [syncMessageType, setSyncMessageType] = useState("info");
  const [viewMode, setViewMode] = useState("schedule");
  const [dayWindow, setDayWindow] = useState({ start: 0, end: 0 });
  const [gsheetRenderData, setGsheetRenderData] = useState(null);
  const [gsheetRenderLoading, setGsheetRenderLoading] = useState(false);
  const [gsheetRenderError, setGsheetRenderError] = useState("");
  const scrollRafRef = useRef(null);
  const autoSaveTimersRef = useRef({});
  const savingRef = useRef({});
  const handleSaveRef = useRef(null);
  const isDraggingRef = useRef(false);
  const dragStartXRef = useRef(0);
  const dragStartScrollRef = useRef(0);
  const dragClientXRef = useRef(0);
  const dragRafRef = useRef(null);
  const dragLastWindowUpdateRef = useRef(0);
  const dragVisitedStartRef = useRef(0);
  const dragVisitedEndRef = useRef(0);
  const syncPollRef = useRef(null);
  const location = useLocation();

  const hasPendingDrafts = useCallback(() => {
    const drafts = draftsRef.current || {};
    return Object.values(drafts).some(
      (userDrafts) => userDrafts && Object.keys(userDrafts).length > 0
    );
  }, []);

  const normalizedScheduleEndpoint = useMemo(() => {
    const raw = String(scheduleEndpoint || "/statistic/schedule/");
    return raw.endsWith("/") ? raw : `${raw}/`;
  }, [scheduleEndpoint]);

  const url = currentMonth
    ? `${normalizedScheduleEndpoint}?month=${currentMonth}`
    : normalizedScheduleEndpoint;

  const scheduleCacheKey = useMemo(
    () => `workScheduleDataCache:${url}`,
    [url]
  );
  const commentsCacheKey = useMemo(
    () =>
      `workScheduleCommentsCache:${normalizedScheduleEndpoint}:${usersEndpoint}`,
    [normalizedScheduleEndpoint, usersEndpoint]
  );

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(scheduleCacheKey);
      if (!raw) {
        setCachedScheduleData(null);
        return;
      }
      const parsed = JSON.parse(raw);
      if (
        !parsed ||
        !Array.isArray(parsed.data) ||
        !Number.isFinite(parsed.ts) ||
        Date.now() - parsed.ts > SCHEDULE_CACHE_TTL_MS
      ) {
        setCachedScheduleData(null);
        return;
      }
      setCachedScheduleData(parsed.data);
    } catch {
      setCachedScheduleData(null);
    }
  }, [scheduleCacheKey]);

  const {
    data,
    loading,
    error,
    refetch: refetchSchedule,
  } = useFetchWithAuth(url);

  useEffect(() => {
    if (typeof refetchSchedule !== "function") return undefined;
    const handleFocus = () => {
      if (
        document.visibilityState === "visible" &&
        !hasPendingDrafts() &&
        !Object.values(savingRef.current || {}).some(Boolean)
      ) {
        refetchSchedule();
      }
    };
    const intervalId = setInterval(() => {
      if (
        document.visibilityState === "visible" &&
        !hasPendingDrafts() &&
        !Object.values(savingRef.current || {}).some(Boolean)
      ) {
        refetchSchedule();
      }
    }, 60000);
    window.addEventListener("focus", handleFocus);
    return () => {
      clearInterval(intervalId);
      window.removeEventListener("focus", handleFocus);
    };
  }, [hasPendingDrafts, refetchSchedule]);

  useEffect(() => {
    if (!Array.isArray(data)) return;
    try {
      sessionStorage.setItem(
        scheduleCacheKey,
        JSON.stringify({ ts: Date.now(), data })
      );
      setCachedScheduleData(data);
    } catch {
      // ignore cache errors
    }
  }, [data, scheduleCacheKey]);

  const includeAllOperators = !readOnly && !filterUserId;
  const scheduleData = Array.isArray(data) ? data : cachedScheduleData;

  useEffect(() => {
    if (!includeAllOperators) {
      setOperators([]);
      return;
    }

    let isActive = true;
    const loadOperators = async () => {
      let hasCachedOperators = false;
      try {
        const raw = sessionStorage.getItem(OPERATORS_CACHE_KEY);
        if (raw) {
          const parsed = JSON.parse(raw);
          if (Array.isArray(parsed) && parsed.length) {
            hasCachedOperators = true;
            if (isActive) setOperators(parsed);
          }
        }
      } catch {
        // ignore cache errors
      }

      setOperatorsLoading(!hasCachedOperators);
      try {
        const response = await fetchWithAuth(`${API_URL}${usersEndpoint}`);
        if (!response.ok) throw new Error("Помилка запиту");
        const json = await response.json();
        if (isActive) {
          const list = Array.isArray(json) ? json : [];
          setOperators(list);
          try {
            sessionStorage.setItem(OPERATORS_CACHE_KEY, JSON.stringify(list));
          } catch {
            // ignore cache errors
          }
        }
      } catch (err) {
        console.error(err);
        if (isActive && !hasCachedOperators) {
          setOperators([]);
        }
      } finally {
        if (isActive) setOperatorsLoading(false);
      }
    };

    loadOperators();

    return () => {
      isActive = false;
    };
  }, [includeAllOperators, usersEndpoint]);

  useEffect(() => {
    if (month !== undefined) {
      setCurrentMonth(normalizeMonth(month));
    }
  }, [month]);

  useEffect(() => {
    if (viewMode !== "table") return undefined;
    let isActive = true;
    const loadGsheetRender = async () => {
      const monthValue = normalizeMonth(currentMonth);
      setGsheetRenderLoading(true);
      setGsheetRenderError("");
      try {
        const response = await fetchWithAuth(
          `${GSHEET_RENDER_ENDPOINT}?month=${encodeURIComponent(monthValue)}`
        );
        if (response.ok) {
          const data = await response.json();
          if (!isActive) return;
          setGsheetRenderData(data);
          setGsheetRenderError("");
          return;
        }
        let payload = null;
        try {
          payload = await response.json();
        } catch {
          // ignore parse errors
        }
        if (!isActive) return;
        setGsheetRenderData(null);
        setGsheetRenderError(
          extractSyncErrorMessage(payload, "Не вдалося отримати таблицю Google Sheet")
        );
      } catch {
        if (!isActive) return;
        setGsheetRenderData(null);
        setGsheetRenderError("Помилка мережі при завантаженні Google Sheet");
      } finally {
        if (isActive) {
          setGsheetRenderLoading(false);
        }
      }
    };

    loadGsheetRender();
    return () => {
      isActive = false;
    };
  }, [currentMonth, viewMode]);

  useEffect(() => {
    savingRef.current = saving;
  }, [saving]);

  useEffect(() => {
    return () => {
      Object.values(autoSaveTimersRef.current).forEach((timerId) => {
        clearTimeout(timerId);
      });
      autoSaveTimersRef.current = {};
    };
  }, []);

  useEffect(() => {
    return () => {
      if (syncPollRef.current) {
        clearInterval(syncPollRef.current);
        syncPollRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (readOnly) {
      setCommentsByUser({});
      return;
    }

    try {
      const raw = sessionStorage.getItem(commentsCacheKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
          setCommentsByUser(parsed);
        }
      }
    } catch {
      // ignore cache errors
    }

    let isActive = true;
    const loadComments = async () => {
      try {
        const response = await fetchWithAuth(
          `${API_URL}/statistic/schedule/comments/`
        );
        if (!response.ok) throw new Error("Помилка запиту");
        const json = await response.json();
        if (!isActive) return;
        const next = {};
        if (Array.isArray(json)) {
          json.forEach((item) => {
            if (!item) return;
            const userId = item.user ?? item.user_id;
            if (userId == null) return;
            next[userId] = item.comment || "";
          });
        }
        // Keep existing notes if backend temporarily returns empty payload
        // (for example when switching month or while data is still syncing).
        setCommentsByUser((prev) => {
          if (Object.keys(next).length === 0) return prev;
          return next;
        });
      } catch (err) {
        console.error(err);
        // keep last known comments on request failure
      }
    };
    loadComments();
    return () => {
      isActive = false;
    };
  }, [commentsCacheKey, readOnly]);

  useEffect(() => {
    if (readOnly) return;
    try {
      sessionStorage.setItem(commentsCacheKey, JSON.stringify(commentsByUser));
    } catch {
      // ignore cache errors
    }
  }, [commentsByUser, commentsCacheKey, readOnly]);

  const flushPendingSaves = useCallback(async () => {
    const idsToSave = new Set();
    Object.entries(draftsRef.current || {}).forEach(([userId, draftMap]) => {
      if (Object.keys(draftMap || {}).length > 0) {
        idsToSave.add(Number(userId));
      }
    });
    Object.keys(autoSaveTimersRef.current || {}).forEach((userId) => {
      idsToSave.add(Number(userId));
    });

    if (!idsToSave.size) return;

    Object.values(autoSaveTimersRef.current).forEach((timerId) => {
      clearTimeout(timerId);
    });
    autoSaveTimersRef.current = {};

    await Promise.allSettled(
      Array.from(idsToSave).map((userId) =>
        handleSaveRef.current?.(userId, { silent: true })
      )
    );
  }, []);

  const stopSyncPolling = useCallback(() => {
    if (!syncPollRef.current) return;
    clearInterval(syncPollRef.current);
    syncPollRef.current = null;
  }, []);

  const getGsheetSyncPayload = useCallback(() => {
    const payload = {
      month: currentMonth,
      dry_run: false,
    };
    return payload;
  }, [currentMonth]);

  const pollSyncStatus = useCallback(async () => {
    try {
      const response = await fetchWithAuth(GSHEET_SYNC_ENDPOINT);
      if (!response.ok) {
        let payload = null;
        try {
          payload = await response.json();
        } catch {
          // ignore parse errors
        }
        stopSyncPolling();
        setSyncRunning(false);
        setSyncMessage(extractSyncErrorMessage(payload, "Помилка перевірки статусу синку"));
        setSyncMessageType("error");
        return;
      }
      const data = await response.json();
      if (data?.running) {
        setSyncRunning(true);
        setSyncMessage("Синхронізація виконується...");
        setSyncMessageType("info");
        return;
      }

      stopSyncPolling();
      setSyncRunning(false);

      if (data?.status === "success") {
        setSyncMessage("Синхронізація завершена успішно");
        setSyncMessageType("success");
        refetchSchedule?.();
        return;
      }

      if (data?.status === "error") {
        setSyncMessage(extractSyncErrorMessage(data, "Помилка синхронізації"));
        setSyncMessageType("error");
      }
    } catch {
      // ignore polling errors, next poll will retry
    }
  }, [refetchSchedule, stopSyncPolling]);

  const startSyncPolling = useCallback(() => {
    stopSyncPolling();
    syncPollRef.current = setInterval(() => {
      pollSyncStatus();
    }, 2500);
  }, [pollSyncStatus, stopSyncPolling]);

  const handleSyncGsheet = useCallback(async () => {
    if (syncRunning) return;
    setSyncMessage("");
    setSyncMessageType("info");

    try {
      const response = await fetchWithAuth(GSHEET_SYNC_ENDPOINT, {
        method: "POST",
        body: getGsheetSyncPayload(),
      });

      if (response.status === 202) {
        setSyncRunning(true);
        setSyncMessage("Синхронізація запущена");
        setSyncMessageType("info");
        await pollSyncStatus();
        startSyncPolling();
        return;
      }

      if (response.status === 409) {
        setSyncRunning(true);
        setSyncMessage("Синк вже виконується");
        setSyncMessageType("info");
        await pollSyncStatus();
        startSyncPolling();
        return;
      }

      let payload = null;
      try {
        payload = await response.json();
      } catch {
        // ignore parse errors
      }
      setSyncRunning(false);
      setSyncMessage(extractSyncErrorMessage(payload, "Не вдалося запустити синхронізацію"));
      setSyncMessageType("error");
    } catch {
      setSyncRunning(false);
      setSyncMessage("Тимчасова помилка мережі при запуску синхронізації");
      setSyncMessageType("error");
    }
  }, [getGsheetSyncPayload, pollSyncStatus, startSyncPolling, syncRunning]);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (!hasPendingDrafts()) return;
      event.preventDefault();
      event.returnValue = "";
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden" && hasPendingDrafts()) {
        flushPendingSaves().catch(() => {});
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [hasPendingDrafts, flushPendingSaves]);

  const changeMonth = async (direction) => {
    await flushPendingSaves();
    setCurrentMonth((prev) =>
      getMonthStart(prev).add(direction, "month").format("MM.YYYY")
    );
  };

  const handleEditComment = useCallback(
    async (userId) => {
      const current = commentsByUser[userId] || "";
      const next = window.prompt("Коментар для оператора:", current);
      if (next === null) return;
      const trimmed = next.trim();
      setCommentsByUser((prev) => ({ ...prev, [userId]: trimmed }));
      try {
        if (!trimmed) {
          await fetchWithAuth(
            `${API_URL}/statistic/schedule/comments/${userId}/`,
            { method: "DELETE" }
          );
          setCommentsByUser((prev) => {
            const copy = { ...prev };
            delete copy[userId];
            return copy;
          });
          return;
        }
        await fetchWithAuth(
          `${API_URL}/statistic/schedule/comments/${userId}/`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ comment: trimmed }),
          }
        );
      } catch (err) {
        console.error(err);
        alert("Не вдалося зберегти коментар");
      }
    },
    [commentsByUser]
  );

  // формируем список дней
  const days = useMemo(() => {
    const m = getMonthStart(currentMonth);
    const daysInMonth = m.daysInMonth();
    return Array.from({ length: daysInMonth }, (_, i) => {
      const d = m.date(i + 1);
      return {
        label: d.format("DD.MM"),
        date: d.format("YYYY-MM-DD"),
      };
    });
  }, [currentMonth]);

  const orderedDays = useMemo(() => {
    return dayOrder === "desc" ? [...days].reverse() : days;
  }, [days, dayOrder]);

  const orderedDates = useMemo(
    () => orderedDays.map((day) => day.date),
    [orderedDays]
  );
  const orderedDatesSet = useMemo(
    () => new Set(orderedDates),
    [orderedDates]
  );

  const handleClearOperatorSchedule = useCallback(async () => {
    const userId = Number(selectedClearUserId);
    if (!Number.isFinite(userId) || clearingSchedule) return;

    const operator = operatorsList.find((person) => person.user === userId);
    const operatorName = operator?.name || `ID ${userId}`;
    const monthValue = normalizeMonth(currentMonth);
    const confirmed = window.confirm(
      `Очистити графік оператора "${operatorName}" за ${monthValue}?`
    );
    if (!confirmed) return;

    setClearingSchedule(true);
    setSyncMessage("");
    setSyncMessageType("info");

    try {
      await flushPendingSaves();
      const response = await fetchWithAuth(
        `${API_URL}/statistic/schedule/users/${userId}/clear/?month=${encodeURIComponent(
          monthValue
        )}`,
        { method: "DELETE" }
      );

      let payload = null;
      try {
        payload = await response.json();
      } catch {
        // ignore parse errors
      }

      if (!response.ok) {
        setSyncMessage(extractSyncErrorMessage(payload, "Не вдалося очистити графік"));
        setSyncMessageType("error");
        return;
      }

      draftsRef.current[userId] = {};
      setScheduleByUser((prev) => ({ ...prev, [userId]: {} }));
      setCachedScheduleData((prev) => {
        if (!Array.isArray(prev)) return prev;
        const next = prev.filter((entry) => {
          const entryUserId = getEntryUserId(entry);
          return !(entryUserId === userId && orderedDatesSet.has(entry?.date));
        });
        try {
          sessionStorage.setItem(
            scheduleCacheKey,
            JSON.stringify({ ts: Date.now(), data: next })
          );
        } catch {
          // ignore cache errors
        }
        return next;
      });

      const deleted = payload?.data?.schedule_deleted;
      setSyncMessage(
        deleted != null
          ? `Графік "${operatorName}" очищено. Видалено записів: ${deleted}`
          : `Графік "${operatorName}" очищено`
      );
      setSyncMessageType("success");
    } catch {
      setSyncMessage("Тимчасова помилка мережі при очистці графіка");
      setSyncMessageType("error");
    } finally {
      setClearingSchedule(false);
    }
  }, [
    clearingSchedule,
    currentMonth,
    flushPendingSaves,
    operatorsList,
    orderedDatesSet,
    scheduleCacheKey,
    selectedClearUserId,
  ]);

  const updateDayWindow = useCallback((options = {}) => {
    const { expandOnly = false } = options;
    const container = tableRef.current;
    if (!container) return;
    const viewportWidth = container.clientWidth || 0;
    const scrollLeft = container.scrollLeft || 0;
    const visibleCount =
      Math.ceil(viewportWidth / DAY_COL_WIDTH) + DAY_OVERSCAN * 2;
    const rawStart = Math.max(
      0,
      Math.floor(scrollLeft / DAY_COL_WIDTH) - DAY_OVERSCAN
    );
    // Update window in chunks to avoid frequent remounts while dragging.
    const nextStart = Math.floor(rawStart / DAY_WINDOW_CHUNK) * DAY_WINDOW_CHUNK;
    const nextEnd = Math.min(orderedDays.length, nextStart + visibleCount);

    if (expandOnly) {
      const expandedStart = Math.max(
        0,
        Math.min(dragVisitedStartRef.current, nextStart)
      );
      const expandedEnd = Math.min(
        orderedDays.length,
        Math.max(dragVisitedEndRef.current, nextEnd)
      );
      dragVisitedStartRef.current = expandedStart;
      dragVisitedEndRef.current = expandedEnd;
      setDayWindow((prev) =>
        prev.start === expandedStart && prev.end === expandedEnd
          ? prev
          : { start: expandedStart, end: expandedEnd }
      );
      return;
    }

    dragVisitedStartRef.current = nextStart;
    dragVisitedEndRef.current = nextEnd;
    setDayWindow((prev) =>
      prev.start === nextStart && prev.end === nextEnd
        ? prev
        : { start: nextStart, end: nextEnd }
    );
  }, [orderedDays.length]);

  const handleDragStart = useCallback((event) => {
    if (event.button !== 0) return;
    const target = event.target;
    if (
      target?.closest(
        "input, textarea, button, [contenteditable='true'], .ws-input"
      )
    ) {
      return;
    }
    const container = tableRef.current;
    if (!container) return;
    isDraggingRef.current = true;
    dragStartXRef.current = event.clientX;
    dragClientXRef.current = event.clientX;
    dragStartScrollRef.current = container.scrollLeft;
    dragLastWindowUpdateRef.current = Date.now();
    dragVisitedStartRef.current = dayWindow.start;
    dragVisitedEndRef.current = dayWindow.end;
    container.classList.add("ws-dragging");
    document.body.style.userSelect = "none";
    event.preventDefault();
  }, [dayWindow.end, dayWindow.start]);

  useEffect(() => {
    const handleDragMove = (event) => {
      if (!isDraggingRef.current) return;
      dragClientXRef.current = event.clientX;
      if (dragRafRef.current) return;
      dragRafRef.current = requestAnimationFrame(() => {
        dragRafRef.current = null;
        if (!isDraggingRef.current) return;
        const container = tableRef.current;
        if (!container) return;
        const delta = dragClientXRef.current - dragStartXRef.current;
        container.scrollLeft = dragStartScrollRef.current - delta;
      });
    };

    const handleDragEnd = () => {
      if (!isDraggingRef.current) return;
      isDraggingRef.current = false;
      if (dragRafRef.current) {
        cancelAnimationFrame(dragRafRef.current);
        dragRafRef.current = null;
      }
      const container = tableRef.current;
      if (container) {
        container.classList.remove("ws-dragging");
      }
      document.body.style.userSelect = "";
      // Compact window after drag ends.
      updateDayWindow();
    };

    window.addEventListener("mousemove", handleDragMove);
    window.addEventListener("mouseup", handleDragEnd);
    window.addEventListener("mouseleave", handleDragEnd);
    return () => {
      if (dragRafRef.current) {
        cancelAnimationFrame(dragRafRef.current);
        dragRafRef.current = null;
      }
      window.removeEventListener("mousemove", handleDragMove);
      window.removeEventListener("mouseup", handleDragEnd);
      window.removeEventListener("mouseleave", handleDragEnd);
      document.body.style.userSelect = "";
    };
  }, [updateDayWindow]);

  useEffect(() => {
    updateDayWindow();
  }, [updateDayWindow, orderedDays.length]);

  useEffect(() => {
    const handleResize = () => updateDayWindow();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [updateDayWindow]);

  const filteredData = useMemo(() => {
    if (!Array.isArray(scheduleData)) return [];
    if (filterUserId) {
      return scheduleData.filter(
        (entry) => getEntryUserId(entry) === filterUserId
      );
    }
    return scheduleData;
  }, [scheduleData, filterUserId]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(operatorSearch);
    }, 300);

    return () => clearTimeout(timer);
  }, [operatorSearch]);

  useEffect(() => {
    const params = new URLSearchParams(location.search || "");
    const operator = params.get("operator");
    const operatorIdRaw = params.get("operator_id");
    const parsedOperatorId = Number(operatorIdRaw);
    setUrlOperatorId(
      Number.isFinite(parsedOperatorId) ? parsedOperatorId : null
    );
    if (operator) {
      setOperatorSearch(operator);
      setSearchQuery(operator);
    }
  }, [location.search]);

  const normalizedSearch = searchQuery.trim().toLowerCase();
  const normalizedSearchKey = normalizeSearchValue(searchQuery);
  const visibleData = useMemo(() => {
    if (urlOperatorId != null) {
      const byId = operatorsList.filter((person) => person.user === urlOperatorId);
      if (byId.length) return byId;
    }
    if (!normalizedSearch) return operatorsList;
    return operatorsList.filter((person) =>
      person.nameLower.includes(normalizedSearch) ||
      person.searchKey.includes(normalizedSearchKey)
    );
  }, [operatorsList, normalizedSearch, normalizedSearchKey, urlOperatorId]);

  useEffect(() => {
    if (!selectedClearUserId) return;
    const selectedId = Number(selectedClearUserId);
    const exists = operatorsList.some((person) => person.user === selectedId);
    if (!exists) {
      setSelectedClearUserId("");
    }
  }, [operatorsList, selectedClearUserId]);

  const selectedClearOperator = useMemo(() => {
    if (!selectedClearUserId) return null;
    const selectedId = Number(selectedClearUserId);
    return operatorsList.find((person) => person.user === selectedId) || null;
  }, [operatorsList, selectedClearUserId]);

  const periodCountsByDay = useMemo(() => {
    const counts = {};
    orderedDays.forEach((day) => {
      counts[day.date] = { morning: 0, day: 0, evening: 0 };
    });

    visibleData.forEach((person) => {
      const scheduleMap = scheduleByUser[person.user];
      if (!scheduleMap) return;
      Object.values(scheduleMap).forEach((entry) => {
        if (!entry?.shift) return;
        if (!orderedDatesSet.has(entry.date)) return;
        const periodKey = classifyShiftPeriod(entry.shift);
        if (!periodKey) return;
        counts[entry.date][periodKey] += 1;
      });
    });

    return counts;
  }, [orderedDays, scheduleByUser, visibleData, orderedDatesSet]);

  // группировка данных
  React.useEffect(() => {
    Object.values(autoSaveTimersRef.current).forEach((timerId) => {
      clearTimeout(timerId);
    });
    autoSaveTimersRef.current = {};

    if (!Array.isArray(filteredData)) {
      setOperatorsList([]);
      setScheduleByUser({});
      draftsRef.current = {};
      return;
    }

    const nextScheduleByUser = {};
    const userNames = {};

    filteredData.forEach((entry) => {
      const u = entry.user ?? entry.user_id ?? entry.operator;
      const userId = getEntryUserId(entry);
      if (!userId) return;
      const name =
        typeof u === "object" && u !== null
          ? `${u.first_name || ""} ${u.last_name || ""}`.trim() ||
            u.username ||
            "-"
          : "-";

      userNames[userId] = name;

      if (!nextScheduleByUser[userId]) {
        nextScheduleByUser[userId] = {};
      }

      nextScheduleByUser[userId][entry.date] = {
        id: entry.id,
        date: entry.date,
        shift: entry.shift || "",
        plan_people: entry.plan_people ?? "",
        fact_people: entry.fact_people ?? "",
      };
    });

    const orderedUserIds = [];
    const seenUsers = new Set();
    filteredData.forEach((entry) => {
      const userId = getEntryUserId(entry);
      if (!userId || seenUsers.has(userId)) return;
      seenUsers.add(userId);
      orderedUserIds.push(userId);
    });

    if (includeAllOperators) {
      const operatorById = new Map(
        (operators || []).map((op) => {
          const opName =
            `${op.first_name || ""} ${op.last_name || ""}`.trim() ||
            op.username ||
            "-";
          return [op.id, opName];
        })
      );

      const fromSchedule = orderedUserIds.map((id) => {
        const resolvedName = operatorById.get(id) || userNames[id] || "-";
        return {
          user: Number(id),
          name: resolvedName,
          nameLower: resolvedName.toLowerCase(),
          searchKey: normalizeSearchValue(resolvedName),
        };
      });

      const missingOperators = (operators || [])
        .filter((op) => !seenUsers.has(op.id))
        .map((op) => {
          const opName =
            `${op.first_name || ""} ${op.last_name || ""}`.trim() ||
            op.username ||
            "-";
          return {
            user: op.id,
            name: opName,
            nameLower: opName.toLowerCase(),
            searchKey: normalizeSearchValue(opName),
          };
        });

      setOperatorsList(
        fromSchedule.length ? [...fromSchedule, ...missingOperators] : missingOperators
      );
    } else {
      const list = orderedUserIds.map((id) => {
        const resolvedName = userNames[id] || "-";
        return {
          user: Number(id),
          name: resolvedName,
          nameLower: resolvedName.toLowerCase(),
          searchKey: normalizeSearchValue(resolvedName),
        };
      });
      setOperatorsList(list);
    }

    setScheduleByUser(nextScheduleByUser);
    draftsRef.current = {};
  }, [filteredData, includeAllOperators, operators]);

  const balanceByUser = useMemo(() => {
    const result = {};
    const today = dayjs().startOf("day");
    Object.entries(scheduleByUser).forEach(([userId, scheduleMap]) => {
      let planSum = 0;
      let factSum = 0;
      let hasPlan = false;
      let hasFact = false;
      Object.values(scheduleMap || {}).forEach((entry) => {
        if (!entry || !orderedDatesSet.has(entry.date)) return;
        const shiftValue = entry.shift ?? "";
        const factValue = parseFactValue(entry.fact_people);
        const dayValue = dayjs(entry.date);
        const isFuture = dayValue.isAfter(today, "day");
        if (isFuture && factValue == null) return;
        if (factValue == null) return;
        const planHours = planHoursFromShift(shiftValue);
        if (planHours != null) {
          hasPlan = true;
          planSum += planHours;
        }
        hasFact = true;
        factSum += factValue;
      });
      if (!hasPlan && !hasFact) return;
      result[userId] = factSum - planSum;
    });
    return result;
  }, [scheduleByUser, orderedDatesSet]);

  const changeShift = useCallback((userId, date, value) => {
    setScheduleByUser((prev) => {
      const userSchedule = { ...(prev[userId] || {}) };
      const existing = userSchedule[date] || {
        id: null,
        date,
        shift: "",
        plan_people: "",
        fact_people: "",
      };
      userSchedule[date] = { ...existing, shift: value };
      return { ...prev, [userId]: userSchedule };
    });
  }, []);

  const changeFactPeople = useCallback((userId, date, value) => {
    setScheduleByUser((prev) => {
      const userSchedule = { ...(prev[userId] || {}) };
      const existing = userSchedule[date] || {
        id: null,
        date,
        shift: "",
        plan_people: "",
        fact_people: "",
      };
      userSchedule[date] = { ...existing, fact_people: value };
      return { ...prev, [userId]: userSchedule };
    });
  }, []);

  const queueAutoSave = useCallback(
    (userId) => {
      if (readOnly) return;
      const existingTimer = autoSaveTimersRef.current[userId];
      if (existingTimer) {
        clearTimeout(existingTimer);
      }
      autoSaveTimersRef.current[userId] = setTimeout(async () => {
        delete autoSaveTimersRef.current[userId];
        if (savingRef.current[userId]) {
          queueAutoSave(userId);
          return;
        }
        if (typeof handleSaveRef.current === "function") {
          await handleSaveRef.current(userId, { silent: true });
        }
      }, AUTOSAVE_DELAY_MS);
    },
    [readOnly]
  );

  const handleDraftChange = useCallback(
    (userId, date, field, value) => {
      const userDrafts = draftsRef.current[userId] || {};
      const dateDraft = userDrafts[date] || {};
      const scheduleEntry = scheduleByUser[userId]?.[date] || {};
      const baseValue = scheduleEntry[field] ?? "";

      const nextFieldValue = normalizeDraftFieldValue(field, value);
      const baseFieldValue = normalizeDraftFieldValue(field, baseValue);

      const nextDateDraft = { ...dateDraft };
      if (nextFieldValue === baseFieldValue) {
        delete nextDateDraft[field];
      } else {
        nextDateDraft[field] = value;
      }

      const nextUserDrafts = { ...userDrafts };
      if (Object.keys(nextDateDraft).length === 0) {
        delete nextUserDrafts[date];
      } else {
        nextUserDrafts[date] = nextDateDraft;
      }

      if (Object.keys(nextUserDrafts).length === 0) {
        delete draftsRef.current[userId];
        const existingTimer = autoSaveTimersRef.current[userId];
        if (existingTimer) {
          clearTimeout(existingTimer);
          delete autoSaveTimersRef.current[userId];
        }
        return;
      }

      draftsRef.current[userId] = nextUserDrafts;
      queueAutoSave(userId);
    },
    [queueAutoSave, scheduleByUser]
  );

  const handleSave = useCallback(async (userId, options = {}) => {
    const { silent = false } = options;
    const existingTimer = autoSaveTimersRef.current[userId];
    if (existingTimer) {
      clearTimeout(existingTimer);
      delete autoSaveTimersRef.current[userId];
    }
    const person = operatorsList.find((entry) => entry.user === userId);
    if (!person) return;
    setSaving((prev) => ({ ...prev, [userId]: true }));
    const scheduleMap = scheduleByUser[userId] || {};
    const draftMap = draftsRef.current[userId] || {};
    const changedEntries = Object.entries(draftMap)
      .map(([date, changes]) => {
        const existing = scheduleMap[date] || {
          id: null,
          date,
          shift: "",
          plan_people: "",
          fact_people: "",
        };
        return { date, entry: { ...existing, ...changes } };
      })
      .filter(({ entry }) => {
        const factRaw = String(entry.fact_people ?? "").trim();
        const factValue = factRaw === "" ? null : Number(factRaw);
        const normalizedFact = Number.isFinite(factValue) ? factValue : null;
        return Boolean(entry.id) || Boolean(entry.shift) || normalizedFact !== null;
      });

    try {
      const entriesNeedingId = changedEntries
        .map(({ entry }) => entry)
        .filter((entry) => !entry.id);

      if (entriesNeedingId.length) {
        try {
          const response = await fetchWithAuth(`${API_URL}${url}`);
          if (response.ok) {
            const json = await response.json();
            const list = Array.isArray(json)
              ? json
              : Array.isArray(json?.data)
              ? json.data
              : [];
            const idMap = new Map();
            list.forEach((entry) => {
              const existingUserId = getEntryUserId(entry);
              if (!existingUserId || !entry?.date || !entry?.id) return;
              idMap.set(`${existingUserId}|${entry.date}`, entry.id);
            });
            entriesNeedingId.forEach((entry) => {
              const key = `${userId}|${entry.date}`;
              const foundId = idMap.get(key);
              if (foundId) entry.id = foundId;
            });
          }
        } catch (err) {
          console.error(err);
        }
      }

      const requests = changedEntries
        .map(({ date, entry }) => {
          const factRaw = String(entry.fact_people ?? "").trim();
          const factValue = factRaw === "" ? null : Number(factRaw);
          const normalizedFact = Number.isFinite(factValue) ? factValue : null;
          if (!entry.id && !entry.shift && normalizedFact === null) {
            return null;
          }

          const payload = {
            user: userId,
            date: entry.date,
            shift: entry.shift,
            fact_people: normalizedFact,
          };

          if (entry.id) {
            return {
              date,
              entry,
              request: fetchWithAuth(
                `${API_URL}${normalizedScheduleEndpoint}${entry.id}/`,
                {
                  method: "PATCH",
                  body: JSON.stringify(payload),
                  headers: { "Content-Type": "application/json" },
                }
              ),
            };
          }

          return {
            date,
            entry,
            request: fetchWithAuth(`${API_URL}${normalizedScheduleEndpoint}`, {
              method: "POST",
              body: JSON.stringify(payload),
              headers: { "Content-Type": "application/json" },
            }),
          };
        })
        .filter(Boolean);

      if (!requests.length) {
        draftsRef.current[userId] = {};
        return;
      }

      const results = await Promise.all(
        requests.map(async ({ date, entry, request }) => {
          const response = await request;
          if (!response.ok) {
            const message = await response.text();
            throw new Error(message || "Помилка запиту");
          }
          const data = await response.json();
          return { date, entry, data };
        })
      );

      setScheduleByUser((prev) => {
        const userSchedule = { ...(prev[userId] || {}) };
        results.forEach(({ date, entry, data }) => {
          userSchedule[date] = {
            id: data?.id ?? entry.id ?? null,
            date: data?.date ?? entry.date ?? date,
            shift: data?.shift ?? entry.shift ?? "",
            plan_people: entry.plan_people ?? "",
            fact_people:
              data?.fact_people ?? entry.fact_people ?? "",
          };
        });
        return { ...prev, [userId]: userSchedule };
      });
      draftsRef.current[userId] = {};
      setCachedScheduleData((prev) => {
        if (!Array.isArray(prev)) return prev;
        const next = [...prev];
        results.forEach(({ date, entry, data }) => {
          const nextEntry = {
            ...(entry || {}),
            ...(data || {}),
            id: data?.id ?? entry?.id ?? null,
            user: data?.user ?? entry?.user ?? userId,
            date: data?.date ?? entry?.date ?? date,
            shift: data?.shift ?? entry?.shift ?? "",
            plan_people: data?.plan_people ?? entry?.plan_people ?? "",
            fact_people: data?.fact_people ?? entry?.fact_people ?? "",
          };

          const targetId = nextEntry.id;
          const targetDate = nextEntry.date;
          const foundIndex = next.findIndex((item) => {
            if (!item) return false;
            if (targetId && item.id === targetId) return true;
            const itemUserId = getEntryUserId(item);
            return itemUserId === userId && item.date === targetDate;
          });

          if (foundIndex >= 0) {
            next[foundIndex] = { ...next[foundIndex], ...nextEntry };
          } else {
            next.push(nextEntry);
          }
        });

        try {
          sessionStorage.setItem(
            scheduleCacheKey,
            JSON.stringify({ ts: Date.now(), data: next })
          );
        } catch {
          // ignore cache errors
        }
        return next;
      });
    } catch (err) {
      console.error(err);
      if (!silent) {
        alert(`Помилка при збереженні для ${person.name}`);
      } else {
        queueAutoSave(userId);
      }
    } finally {
      setSaving((prev) => ({ ...prev, [userId]: false }));
    }
  }, [
    operatorsList,
    scheduleByUser,
    url,
    normalizedScheduleEndpoint,
    queueAutoSave,
    scheduleCacheKey,
  ]);

  useEffect(() => {
    handleSaveRef.current = handleSave;
  }, [handleSave]);

  const pagedData = visibleData;

  const dayTotalsByDate = useMemo(() => {
    const totals = {};
    orderedDays.forEach((day) => {
      totals[day.date] = { plan: 0, fact: 0 };
    });
    visibleData.forEach((person) => {
      const scheduleMap = scheduleByUser[person.user];
      if (!scheduleMap) return;
      Object.values(scheduleMap).forEach((entry) => {
        if (!entry || !orderedDatesSet.has(entry.date)) return;
        totals[entry.date].plan += getPlanNumeric(entry, entry.shift ?? "");
        const factValue = parseFactValue(entry.fact_people);
        if (factValue != null) {
          totals[entry.date].fact += factValue;
        }
      });
    });
    return totals;
  }, [orderedDays, scheduleByUser, visibleData, orderedDatesSet]);

  const gsheetGrid = useMemo(() => {
    if (!gsheetRenderData || typeof gsheetRenderData !== "object") return null;

    const rowCount = Number(gsheetRenderData.row_count) || 0;
    const colCount = Number(gsheetRenderData.col_count) || 0;
    if (rowCount <= 0 || colCount <= 0) return null;

    const cellsRaw = Array.isArray(gsheetRenderData.cells)
      ? gsheetRenderData.cells
      : [];
    const mergesRaw = Array.isArray(gsheetRenderData.merges)
      ? gsheetRenderData.merges
      : [];
    const columnWidths = Array.isArray(gsheetRenderData.column_widths)
      ? gsheetRenderData.column_widths
      : [];
    const rowHeights = Array.isArray(gsheetRenderData.row_heights)
      ? gsheetRenderData.row_heights
      : [];

    const cellMap = new Map();
    const mergeMap = new Map();
    const mergedInner = new Set();

    const getIndex = (obj, keys) => {
      for (const key of keys) {
        const value = Number(obj?.[key]);
        if (Number.isFinite(value)) return value;
      }
      return null;
    };

    const sampleCell = !Array.isArray(cellsRaw[0])
      ? cellsRaw.find((cell) => cell && typeof cell === "object")
      : null;
    const sampleMerge = mergesRaw.find((merge) => merge && typeof merge === "object");

    const cellRowSample = sampleCell ? getIndex(sampleCell, ["r", "row", "row_index"]) : null;
    const cellColSample = sampleCell ? getIndex(sampleCell, ["c", "col", "col_index"]) : null;
    const mergeRowSample = sampleMerge ? getIndex(sampleMerge, ["r1", "start_row", "row_start"]) : null;
    const mergeColSample = sampleMerge ? getIndex(sampleMerge, ["c1", "start_col", "col_start"]) : null;
    const zeroBased =
      cellRowSample === 0 ||
      cellColSample === 0 ||
      mergeRowSample === 0 ||
      mergeColSample === 0;
    const normalizeIndex = (value) =>
      zeroBased && Number.isFinite(value) ? value + 1 : value;

    if (cellsRaw.length && Array.isArray(cellsRaw[0])) {
      cellsRaw.forEach((row, rowIndex) => {
        row.forEach((cell, colIndex) => {
          if (!cell || typeof cell !== "object") return;
          cellMap.set(`${rowIndex + 1}:${colIndex + 1}`, cell);
        });
      });
    } else {
      cellsRaw.forEach((cell) => {
        if (!cell || typeof cell !== "object") return;
        const r = normalizeIndex(getIndex(cell, ["r", "row", "row_index"]));
        const c = normalizeIndex(getIndex(cell, ["c", "col", "col_index"]));
        if (!Number.isFinite(r) || !Number.isFinite(c)) return;
        cellMap.set(`${r}:${c}`, cell);
      });
    }

    mergesRaw.forEach((merge) => {
      if (!merge || typeof merge !== "object") return;
      const r1 = normalizeIndex(getIndex(merge, ["r1", "start_row", "row_start"]));
      const c1 = normalizeIndex(getIndex(merge, ["c1", "start_col", "col_start"]));
      const r2 = normalizeIndex(getIndex(merge, ["r2", "end_row", "row_end"]));
      const c2 = normalizeIndex(getIndex(merge, ["c2", "end_col", "col_end"]));
      if (![r1, c1, r2, c2].every(Number.isFinite)) return;
      if (r2 < r1 || c2 < c1) return;

      const rowSpan = r2 - r1 + 1;
      const colSpan = c2 - c1 + 1;
      const topLeftKey = `${r1}:${c1}`;
      mergeMap.set(topLeftKey, { rowSpan, colSpan });

      for (let r = r1; r <= r2; r += 1) {
        for (let c = c1; c <= c2; c += 1) {
          if (r === r1 && c === c1) continue;
          mergedInner.add(`${r}:${c}`);
        }
      }
    });

    let renderStartCol = 1;
    let renderEndCol = colCount;

    // Match Google-like visible area: if "Ранок" starts at D, render from B.
    const morningCols = [];
    cellMap.forEach((cell, key) => {
      const text = String(cell?.display ?? cell?.v ?? "").trim().toLowerCase();
      if (text !== "ранок") return;
      const [rowPart, colPart] = key.split(":");
      const rowNum = Number(rowPart);
      const colNum = Number(colPart);
      if (!Number.isFinite(rowNum) || !Number.isFinite(colNum)) return;
      if (rowNum > 6) return;
      morningCols.push(colNum);
    });
    if (morningCols.length) {
      const firstMorningCol = Math.min(...morningCols);
      renderStartCol = Math.max(1, firstMorningCol - 2);
    }
    if (renderStartCol > colCount) {
      renderStartCol = 1;
    }
    const renderColCount = Math.max(0, renderEndCol - renderStartCol + 1);

    return {
      rowCount,
      colCount,
      renderStartCol,
      renderEndCol,
      renderColCount,
      cellMap,
      mergeMap,
      mergedInner,
      columnWidths,
      rowHeights,
      title: gsheetRenderData.title || gsheetRenderData.sheet_name || "",
    };
  }, [gsheetRenderData]);

  const gsheetStickyOffsets = useMemo(() => {
    if (!gsheetGrid) return { top: [], left: [] };

    const top = [];
    let topOffset = 0;
    for (let rowIndex = 1; rowIndex <= GSHEET_STICKY_HEADER_ROWS; rowIndex += 1) {
      top[rowIndex] = topOffset;
      const rowHeight = Number(gsheetGrid.rowHeights?.[rowIndex - 1]);
      topOffset += Number.isFinite(rowHeight) && rowHeight > 0
        ? rowHeight
        : GSHEET_DEFAULT_ROW_HEIGHT;
    }

    const left = [];
    let leftOffset = 0;
    for (
      let colIndex = gsheetGrid.renderStartCol;
      colIndex < gsheetGrid.renderStartCol + GSHEET_STICKY_LEFT_COLUMNS;
      colIndex += 1
    ) {
      left[colIndex] = leftOffset;
      const colWidth = Number(gsheetGrid.columnWidths?.[colIndex - 1]);
      leftOffset += Number.isFinite(colWidth) && colWidth > 0 ? colWidth : 36;
    }

    return { top, left };
  }, [gsheetGrid]);

  const getGsheetCellStyle = useCallback((cell, rowNumber, colNumber) => {
    const style = cell?.style || {};
    const effectiveFormat = cell?.effectiveFormat || cell?.effective_format || {};
    const userEnteredFormat =
      cell?.userEnteredFormat || cell?.user_entered_format || {};
    const nestedEffectiveFormat =
      style?.effectiveFormat || style?.effective_format || {};
    const format = {
      ...userEnteredFormat,
      ...effectiveFormat,
      ...nestedEffectiveFormat,
    };
    const border =
      style?.border ||
      style?.borders ||
      format?.border ||
      format?.borders ||
      {};
    const textFormat = {
      ...(format?.textFormat || format?.text_format || {}),
      ...(style?.textFormat || style?.text_format || {}),
    };
    const parseAlign = (value) => {
      const normalized = String(value || "").toLowerCase();
      if (normalized === "center" || normalized === "middle") return "center";
      if (normalized === "right") return "right";
      if (normalized === "left") return "left";
      if (normalized === "top") return "top";
      if (normalized === "bottom") return "bottom";
      return undefined;
    };
    const borderStyle = (value) => {
      if (!value) return undefined;
      if (typeof value === "string") return `1px solid ${value}`;
      if (typeof value === "object") {
        const color =
          pickGsheetColor(
            value.color,
            value.colorStyle?.rgbColor,
            value.foregroundColor,
            value.foregroundColorStyle?.rgbColor,
            value.effectiveColor,
            value.effectiveColorStyle?.rgbColor
          ) || "#111827";
        const styleKey = String(value.style || "").toUpperCase();
        if (styleKey === "NONE") return "none";
        const width =
          Number(value.width) ||
          (styleKey.includes("THICK") ? 2 : 1);
        return `${width}px solid ${color}`;
      }
      return undefined;
    };

    const widthValue = Number(gsheetGrid?.columnWidths?.[colNumber - 1]);
    const normalizedWidth = Number.isFinite(widthValue) && widthValue > 0 ? widthValue : 36;
    const heightValue = Number(gsheetGrid?.rowHeights?.[rowNumber - 1]);
    const normalizedHeight = Number.isFinite(heightValue) && heightValue > 0 ? heightValue : undefined;

    const bgColor = pickGsheetColor(
      style?.bg,
      style?.background,
      style?.backgroundColor,
      style?.background_color,
      style?.backgroundColorStyle?.rgbColor,
      style?.effectiveBackgroundColor,
      style?.effective_background,
      style?.effectiveFormat?.backgroundColor,
      style?.effectiveFormat?.backgroundColorStyle?.rgbColor,
      format?.backgroundColor,
      format?.background,
      format?.background_color,
      format?.backgroundColorStyle?.rgbColor,
      format?.effectiveBackgroundColor,
      format?.effectiveBackgroundColorStyle?.rgbColor,
      cell?.backgroundColor,
      cell?.backgroundColorStyle?.rgbColor,
      cell?.effectiveBackgroundColor,
      cell?.effectiveBackgroundColorStyle?.rgbColor
    );

    const fgColor = pickGsheetColor(
      style?.color,
      style?.foregroundColor,
      style?.foreground_color,
      style?.textColor,
      style?.text_color,
      textFormat?.foregroundColor,
      textFormat?.foregroundColorStyle?.rgbColor,
      textFormat?.color,
      textFormat?.colorStyle?.rgbColor,
      cell?.foregroundColor,
      cell?.foregroundColorStyle?.rgbColor
    );

    const getCellText = (row, col) => {
      const target = gsheetGrid?.cellMap?.get(`${row}:${col}`);
      if (!target) return "";
      const value = target.display ?? target.v ?? "";
      return String(value).trim();
    };

    const headerCellText = getCellText(4, colNumber).toLowerCase();
    const isShiftCol = headerCellText.includes("граф");
    const isPlanCol = headerCellText.includes("люд");
    const isFactCol = headerCellText.includes("факт");
    const isDataRow = rowNumber >= 6;

    const resolveFallbackBackground = () => {
      if (!isDataRow) return undefined;
      if (!(isPlanCol || isFactCol)) return undefined;

      const shiftCol = isPlanCol ? colNumber - 1 : colNumber - 2;
      const planCol = shiftCol + 1;
      const factCol = shiftCol + 2;
      if (shiftCol < 1 || planCol < 1 || factCol < 1) return undefined;

      const shiftValue = getCellText(rowNumber, shiftCol);
      const planText = getCellText(rowNumber, planCol);
      const factText = getCellText(rowNumber, factCol);
      const factNumeric = parseFactValue(factText);
      const planNumeric =
        parsePlanValue(planText) ??
        (toMinutes(shiftValue) ? planHoursFromShiftAll(shiftValue) : null);

      if (isPlanCol && /^[vbв]$/i.test(planText)) {
        // Google-like behavior: B + fact value -> blue; B without fact -> yellow
        return factNumeric == null ? "#f4df8e" : "#a4c2f4";
      }

      const googleLikeColor = (isFactCell) => {
        if (planNumeric == null || factNumeric == null) return "#ffffff";
        const diff = factNumeric - planNumeric;
        if (Math.abs(diff) < 0.01) return "#b7e1cd"; // green
        if (diff > 0) return "#a4c2f4"; // blue
        return "#ea9999"; // red
      };

      if (isPlanCol) {
        return googleLikeColor(false);
      }

      if (isFactCol) {
        return googleLikeColor(true);
      }

      return undefined;
    };

    const isNeutralBackground = (value) => {
      if (!value) return true;
      const normalized = String(value).trim().toLowerCase().replace(/\s+/g, "");
      return (
        normalized === "transparent" ||
        normalized === "#fff" ||
        normalized === "#ffffff" ||
        normalized === "rgb(255,255,255)" ||
        normalized === "rgba(255,255,255,1)" ||
        normalized === "rgba(255,255,255,0)" ||
        normalized === "rgba(0,0,0,0)"
      );
    };

    const fallbackBgColor = resolveFallbackBackground();
    const resolvedBgColor =
      fallbackBgColor && isNeutralBackground(bgColor)
        ? fallbackBgColor
        : bgColor || fallbackBgColor;
    const fallbackTextColor = isDataRow && (isShiftCol || isPlanCol || isFactCol)
      ? "#111827"
      : undefined;

    const fontSizeValue = Number(
      style?.font_size ??
        style?.fontSize ??
        textFormat?.fontSize ??
        textFormat?.font_size
    );

    return {
      minWidth: normalizedWidth,
      maxWidth: normalizedWidth,
      width: normalizedWidth,
      height: normalizedHeight,
      backgroundColor: resolvedBgColor,
      color: fgColor || fallbackTextColor,
      fontWeight: (style?.bold ?? textFormat?.bold) ? 700 : undefined,
      fontStyle: (style?.italic ?? textFormat?.italic) ? "italic" : undefined,
      fontSize: Number.isFinite(fontSizeValue) && fontSizeValue > 0
        ? `${fontSizeValue}px`
        : "13px",
      textAlign: parseAlign(
        style?.h_align ??
          style?.horizontalAlignment ??
          format?.horizontalAlignment ??
          textFormat?.horizontalAlignment
      ),
      verticalAlign: parseAlign(
        style?.v_align ??
          style?.verticalAlignment ??
          format?.verticalAlignment ??
          textFormat?.verticalAlignment
      ),
      whiteSpace:
        style?.wrap ||
        style?.wrapStrategy ||
        format?.wrap ||
        format?.wrapStrategy
          ? "pre-wrap"
          : "nowrap",
      borderTop: borderStyle(border?.top),
      borderRight: borderStyle(border?.right),
      borderBottom: borderStyle(border?.bottom),
      borderLeft: borderStyle(border?.left),
    };
  }, [gsheetGrid?.cellMap, gsheetGrid?.columnWidths, gsheetGrid?.rowHeights]);

  const hasDayWindow = dayWindow.end > 0;
  const visibleDays = useMemo(() => {
    if (!orderedDays.length) return [];
    if (!hasDayWindow) return orderedDays;
    return orderedDays.slice(dayWindow.start, dayWindow.end);
  }, [orderedDays, dayWindow, hasDayWindow]);

  const leftSpacerWidth = hasDayWindow
    ? dayWindow.start * DAY_COL_WIDTH
    : 0;
  const rightSpacerWidth = hasDayWindow
    ? (orderedDays.length - dayWindow.end) * DAY_COL_WIDTH
    : 0;

  const isLoading =
    (loading && !Array.isArray(cachedScheduleData)) ||
    (includeAllOperators && operatorsLoading && operators.length === 0);
  if (isLoading) return <p>Загрузка...</p>;
  if (error) return <p>Ошибка: {error.message}</p>;

  return (
    <Card>
      <CardContent>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            gap: 2,
          }}
        >
          <Typography variant="h5" fontWeight={600}>
            {title}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                display: "flex",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                overflow: "hidden",
                mr: 0.5,
              }}
            >
              <Button
                size="small"
                variant={viewMode === "schedule" ? "contained" : "text"}
                onClick={() => setViewMode("schedule")}
                sx={{ borderRadius: 0, minWidth: 86 }}
              >
                Графік
              </Button>
              <Button
                size="small"
                variant={viewMode === "table" ? "contained" : "text"}
                onClick={() => setViewMode("table")}
                sx={{ borderRadius: 0, minWidth: 86 }}
              >
                Таблиця
              </Button>
            </Box>
            {!readOnly && (
              <Button
                size="small"
                variant="outlined"
                onClick={handleSyncGsheet}
                disabled={syncRunning}
              >
                {syncRunning ? "Синхронізація..." : "Синхронізувати GSheet"}
              </Button>
            )}
            {!readOnly && selectedClearUserId && (
              <Button
                size="small"
                variant="outlined"
                color="error"
                onClick={handleClearOperatorSchedule}
                disabled={clearingSchedule}
              >
                {clearingSchedule ? "Очищення..." : "Очистити"}
              </Button>
            )}
            {!readOnly && (
              <Autocomplete
                size="small"
                options={operatorsList}
                value={selectedClearOperator}
                getOptionLabel={(option) => option?.name || ""}
                isOptionEqualToValue={(option, value) =>
                  option?.user === value?.user
                }
                onChange={(_, option) =>
                  setSelectedClearUserId(option ? String(option.user) : "")
                }
                noOptionsText="Операторів не знайдено"
                sx={{ minWidth: 220 }}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Оператор для очистки" />
                )}
              />
            )}
            {!readOnly && (
              <TextField
                size="small"
                placeholder="Пошук оператора..."
                value={operatorSearch}
                onChange={(e) => setOperatorSearch(e.target.value)}
                sx={{ minWidth: 220 }}
              />
            )}
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {currentMonth}
            </Typography>
            <Button
              size="small"
              onClick={() => changeMonth(-1)}
              sx={{
                minWidth: 32,
                width: 32,
                height: 32,
                px: 0,
                fontSize: 20,
                lineHeight: 1,
                fontWeight: 700,
              }}
            >
              ←
            </Button>
            <Button
              size="small"
              onClick={() => changeMonth(1)}
              sx={{
                minWidth: 32,
                width: 32,
                height: 32,
                px: 0,
                fontSize: 20,
                lineHeight: 1,
                fontWeight: 700,
              }}
            >
              →
            </Button>
          </Box>
        </Box>
        {!readOnly && syncMessage && (
          <Typography
            variant="body2"
            sx={{
              mb: 1.5,
              color:
                syncMessageType === "error"
                  ? "error.main"
                  : syncMessageType === "success"
                  ? "success.main"
                  : "text.secondary",
              fontWeight: 500,
            }}
          >
            {syncMessage}
          </Typography>
        )}

        {viewMode === "schedule" ? (
          <TableContainer
            component={Paper}
            className="ws-table-container"
            ref={tableRef}
            onMouseDown={handleDragStart}
            onScroll={() => {
              if (isDraggingRef.current) {
                const now = Date.now();
                if (
                  now - dragLastWindowUpdateRef.current <
                  DRAG_WINDOW_UPDATE_THROTTLE_MS
                ) {
                  return;
                }
                dragLastWindowUpdateRef.current = now;
              }
              if (scrollRafRef.current) return;
              scrollRafRef.current = requestAnimationFrame(() => {
                scrollRafRef.current = null;
                updateDayWindow({ expandOnly: isDraggingRef.current });
              });
            }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow className="bg-f6f7f9">
                  <TableCell
                    className={`text-black border-bottom ws-header-name ws-sticky-name ws-sticky-header ${
                      readOnly ? "ws-sticky-name-only" : ""
                    }`}
                  >
                    Ім'я
                  </TableCell>
                  {leftSpacerWidth > 0 && (
                    <TableCell
                      className="text-black border-bottom ws-spacer-cell"
                      style={{ minWidth: leftSpacerWidth }}
                    />
                  )}
                  {visibleDays.map((day) => (
                    <TableCell
                      key={day.date}
                      className="text-black border-bottom ws-header-cell"
                    >
                      <span className="ws-header-weekday">
                        {getWeekdayLabel(day.date)}
                      </span>
                      <span className="ws-header-date">
                        {day.label}
                      </span>
                      <div className="ws-period-grid">
                        {PERIODS.map((period) => (
                          <div className="ws-period-item" key={period.key}>
                            <span className="ws-period-label">
                              {period.label}
                            </span>
                            <span className="ws-period-count">
                              {periodCountsByDay[day.date]?.[period.key] ?? 0}
                            </span>
                          </div>
                        ))}
                      </div>
                      <span className="ws-day-totals">
                        План: {formatHoursValue(dayTotalsByDate[day.date]?.plan ?? 0)}
                        {"  "}
                        Факт: {formatHoursValue(dayTotalsByDate[day.date]?.fact ?? 0)}
                      </span>
                      <span className="ws-header-title bg-f6f7f9">
                        Зміна / План / Факт
                      </span>
                    </TableCell>
                  ))}
                  {rightSpacerWidth > 0 && (
                    <TableCell
                      className="text-black border-bottom ws-spacer-cell"
                      style={{ minWidth: rightSpacerWidth }}
                    />
                  )}
                </TableRow>
              </TableHead>

              <TableBody>
                {pagedData.length ? (
                  pagedData.map((person, index) => (
                    <WorkScheduleRow
                      key={person.user}
                      person={person}
                      orderedDays={orderedDays}
                      visibleDays={visibleDays}
                      dayStartIndex={hasDayWindow ? dayWindow.start : 0}
                      leftSpacerWidth={leftSpacerWidth}
                      rightSpacerWidth={rightSpacerWidth}
                      scheduleMap={scheduleByUser[person.user] || {}}
                      draftsRef={draftsRef}
                      readOnly={readOnly}
                      onDraftChange={handleDraftChange}
                      onShiftChange={changeShift}
                      onFactChange={changeFactPeople}
                      rowIndex={index}
                      rowCount={pagedData.length}
                      tableRef={tableRef}
                      balanceHours={balanceByUser[person.user]}
                      showBalance
                      comment={commentsByUser[person.user] || ""}
                      showComment={!readOnly}
                      onEditComment={handleEditComment}
                    />
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={
                        visibleDays.length +
                        (leftSpacerWidth > 0 ? 1 : 0) +
                        (rightSpacerWidth > 0 ? 1 : 0) +
                        1
                      }
                      sx={{ textAlign: "center", py: 4 }}
                    >
                      {normalizedSearch
                        ? "Немає такого оператора"
                        : "Немає даних"}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box className="ws-gsheet-wrap">
            {gsheetRenderLoading ? (
              <Box className="ws-gsheet-missing">
                <Typography variant="body2">Завантаження таблиці...</Typography>
              </Box>
            ) : gsheetRenderError ? (
              <Box className="ws-gsheet-missing">
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: "error.main" }}>
                  {gsheetRenderError}
                </Typography>
                <Typography variant="caption" sx={{ display: "block" }}>
                  Перевір endpoint: `/api/statistic/schedule/gsheet-sheet-render/?month={currentMonth}`
                </Typography>
              </Box>
            ) : gsheetGrid ? (
              <div className="ws-gsheet-render-wrap">
                <table className="ws-gsheet-render-table">
                  <tbody>
                    {Array.from({ length: gsheetGrid.rowCount }).map((_, rowOffset) => {
                      const rowIndex = rowOffset + 1;
                      return (
                        <tr key={`gs-row-${rowIndex}`}>
                          {Array.from({ length: gsheetGrid.renderColCount }).map((__, colOffset) => {
                            const colIndex = gsheetGrid.renderStartCol + colOffset;
                            const key = `${rowIndex}:${colIndex}`;
                            if (gsheetGrid.mergedInner.has(key)) return null;
                            const cell = gsheetGrid.cellMap.get(key);
                            const merge = gsheetGrid.mergeMap.get(key);
                            const style = getGsheetCellStyle(cell, rowIndex, colIndex);
                            const isStickyHeader =
                              rowIndex <= GSHEET_STICKY_HEADER_ROWS;
                            const isStickyLeft =
                              colOffset < GSHEET_STICKY_LEFT_COLUMNS;
                            const stickyStyle = {
                              ...(isStickyHeader
                                ? { top: gsheetStickyOffsets.top[rowIndex] || 0 }
                                : {}),
                              ...(isStickyLeft
                                ? { left: gsheetStickyOffsets.left[colIndex] || 0 }
                                : {}),
                            };
                            const displayValue = cell?.display ?? cell?.v ?? "";
                            return (
                              <td
                                key={`gs-cell-${key}`}
                                className={[
                                  "ws-gsheet-cell",
                                  isStickyHeader ? "ws-gsheet-sticky-top" : "",
                                  isStickyLeft ? "ws-gsheet-sticky-left" : "",
                                  isStickyHeader && isStickyLeft
                                    ? "ws-gsheet-sticky-corner"
                                    : "",
                                ]
                                  .filter(Boolean)
                                  .join(" ")}
                                rowSpan={merge?.rowSpan || 1}
                                colSpan={merge?.colSpan || 1}
                                style={{ ...style, ...stickyStyle }}
                                title={displayValue ? String(displayValue) : undefined}
                              >
                                {displayValue !== "" && displayValue !== null && displayValue !== undefined
                                  ? String(displayValue)
                                  : "\u00A0"}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <Box className="ws-gsheet-missing">
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Немає даних Google Sheet для цього місяця.
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default WorkScheduleTable;
